import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { X, Crown, Zap, Bell, Users } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

export const PaywallModal = ({ isOpen, onClose, remaining = 0 }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (!isOpen) return null;

  const handleSubscribe = () => {
    if (!user) {
      navigate('/login?redirect=/subscribe');
    } else {
      navigate('/subscribe');
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="paywall-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-fadeIn">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 p-1 hover:bg-slate-100 rounded-full"
          data-testid="paywall-close"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Crown Icon */}
        <div className="flex justify-center mb-4">
          <div className="bg-gradient-to-br from-amber-400 to-amber-600 p-4 rounded-full">
            <Crown className="h-8 w-8 text-white" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-serif font-bold text-center mb-2">
          {remaining === 0 ? "You've reached your daily limit" : "Unlock Unlimited Access"}
        </h2>
        
        <p className="text-slate-600 text-center mb-6">
          {remaining === 0 
            ? "Subscribe to continue reading and get exclusive features"
            : `You have ${remaining} free articles remaining today. Go premium for unlimited access.`
          }
        </p>

        {/* Features */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-slate-100 p-2 rounded-lg">
              <Zap className="h-4 w-4 text-amber-600" />
            </div>
            <span className="text-sm">Unlimited article access</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-slate-100 p-2 rounded-lg">
              <Bell className="h-4 w-4 text-amber-600" />
            </div>
            <span className="text-sm">Instant email alerts for your interests</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-slate-100 p-2 rounded-lg">
              <Users className="h-4 w-4 text-amber-600" />
            </div>
            <span className="text-sm">Refer friends & earn free months</span>
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-slate-50 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <p className="font-semibold">Monthly Premium</p>
              <p className="text-xs text-slate-500">Cancel anytime</p>
            </div>
            <p className="text-2xl font-bold">£3.99<span className="text-sm font-normal text-slate-500">/mo</span></p>
          </div>
        </div>

        {/* CTA */}
        <Button 
          onClick={handleSubscribe}
          className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-6 text-lg"
          data-testid="paywall-subscribe-btn"
        >
          {user ? 'Subscribe Now' : 'Sign Up & Subscribe'}
        </Button>

        <p className="text-center text-xs text-slate-500 mt-4">
          Or <button onClick={onClose} className="underline">continue with limited access</button>
        </p>
      </div>
    </div>
  );
};

export default PaywallModal;
